import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { HoldingsTable } from "@/components/HoldingsTable";
import { StatementImport } from "@/components/StatementImport";
import { TspUpdateButton } from "@/components/TspUpdate";
import { PlaidConnect } from "@/components/PlaidConnect";
import { SyncButton } from "@/pages/Dashboard";
import { PlaidActivity } from "@/components/PlaidActivity";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePortfolio } from "@/context/ProfileContext";
import { api, errMsg, useFetch } from "@/lib/api";

function TspMeta({ meta, onSaved }) {
  const [m, setM] = useState({ loan_balance: 0, agency_match_ytd: 0 });
  useEffect(() => { if (meta) setM({ loan_balance: meta.loan_balance || 0, agency_match_ytd: meta.agency_match_ytd || 0 }); }, [meta]);
  const save = async () => {
    try {
      await api.put("/tsp/meta", { loan_balance: Number(m.loan_balance), agency_match_ytd: Number(m.agency_match_ytd) });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="tsp-meta-form">
      <div className="space-y-1.5"><Label>TSP loan balance</Label><Input type="number" className="font-mono-num" value={m.loan_balance} onChange={(e) => setM({ ...m, loan_balance: e.target.value })} data-testid="tsp-loan-input" /></div>
      <div className="space-y-1.5"><Label>Agency match YTD</Label><Input type="number" className="font-mono-num" value={m.agency_match_ytd} onChange={(e) => setM({ ...m, agency_match_ytd: e.target.value })} data-testid="tsp-match-input" /></div>
      <Button onClick={save} data-testid="tsp-meta-save">Save</Button>
    </div>
  );
}

export default function Accounts() {
  const { dash, loadDash } = usePortfolio();
  const items = useFetch("/plaid/items");
  const refresh = () => { loadDash(); items.reload(); };
  return (
    <div>
      <PageHeader title="Accounts & Sync" subtitle="Link with Plaid, import a statement, or enter fund shares by hand. Valuations use the latest official TSP close and live IRA quotes."
        actions={<SyncButton onDone={refresh} />} />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title="Thrift Savings Plan holdings" className="xl:col-span-2" action={<TspUpdateButton size="sm" testid="accounts-tsp-update-button" />} testid="panel-tsp-holdings">
          <HoldingsTable holdings={dash?.holdings} accounts={["tsp"]} onChange={loadDash} />
          <div className="mt-6 pt-6 border-t border-border"><TspMeta meta={dash?.meta} onSaved={loadDash} /></div>
        </Panel>
        <div className="space-y-6">
          <Panel title="Aggregator link" action={<Badge variant="outline" className="font-mono-num text-[10px]" data-testid="plaid-env-badge">Plaid · {items.data?.env || "..."}</Badge>} testid="panel-plaid">
            <p className="text-sm text-muted-foreground mb-4">Search for "Thrift Savings Plan" or your IRA brokerage in the Plaid window. If the connection fails, use the statement import below.</p>
            <div className="flex flex-wrap gap-2">
              <PlaidConnect onLinked={refresh} />
            </div>
            {items.data?.items?.length > 0 && (
              <ul className="mt-4 space-y-1 text-xs" data-testid="plaid-items-list">
                {items.data.items.map((i) => <li key={i.item_id} className="flex justify-between"><span>{i.institution || "Linked institution"}</span><span className="text-muted-foreground">{i.updated_at?.slice(0, 10)}</span></li>)}
              </ul>
            )}
          </Panel>
          <Panel title="Manual statement import" testid="panel-import"><StatementImport onImported={loadDash} /></Panel>
        </div>
        <Panel title="IRA & external holdings" className="xl:col-span-3" testid="panel-ira-holdings">
          <HoldingsTable holdings={dash?.holdings} accounts={["trad_ira", "roth_ira", "other"]} onChange={loadDash} />
        </Panel>
        <Panel title="Plaid auto-updates" className="xl:col-span-3" testid="panel-plaid-activity">
          <PlaidActivity items={items.data ? { ...items.data, reload: items.reload } : null} />
        </Panel>
      </div>
    </div>
  );
}
